import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { Phone, CheckCircle2, Car, Calendar, AlertTriangle, ShieldCheck, Clock, ChevronRight, Star, MapPin, Lock, Zap, FileCheck, Users, X, Menu } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Toaster, toast } from "sonner";

// For Vercel deployment - API is relative path
const API_SUBMIT_URL = `${process.env.REACT_APP_API_BASE_URL}/api/leads`;
const PHONE_NUMBER = "(803) 403-0488";
const PHONE_LINK = "tel:+18034030488";
const CALL_CTA_CLASS = "call-cta"; // Consistent class for all call buttons

// Google Ads Conversion Labels
// IMPORTANT: Replace these placeholder values with your actual Google Ads conversion labels
// You can find these in Google Ads: Tools & Settings > Conversions > [Your Conversion] > Tag setup
const GOOGLE_ADS_CONVERSION_ID = 'AW-17196366586';
const GOOGLE_ADS_FORM_SUBMIT_LABEL = 'HP5dCIPTq5AcEPr17odA';
const GOOGLE_ADS_CALL_CLICK_LABEL = 'sjZsCP6hrJAcEPr17odA';

// GA4-ready tracking utility with Google Ads conversion support
const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === "undefined") return;

  // 1) GTM Custom Event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  });

  // 2) GA4 direct
  if (window.gtag) {
    window.gtag("event", eventName, eventParams);
  }

  console.log(`[TRACK] ${eventName}`, eventParams);

  // 3) Optional custom event
  window.dispatchEvent(
    new CustomEvent("conversion_event", {
      detail: { eventName, eventParams },
    })
  );
};

// Fire Google Ads conversion for successful form submissions
const trackFormConversion = () => {
  if (typeof window === "undefined" || !window.gtag) return;
  
  // Only fire if conversion label is configured (not placeholder)
  if (GOOGLE_ADS_FORM_SUBMIT_LABEL !== 'PASTE_FORM_SUBMIT_LABEL_HERE') {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_FORM_SUBMIT_LABEL}`,
    });
    console.log('[CONVERSION] Google Ads form submission conversion fired');
  } else {
    console.log('[CONVERSION] Form submit label not configured - skipping Google Ads conversion');
  }
};

// Fire Google Ads conversion for call button clicks
const trackCallConversion = (location) => {
  if (typeof window === "undefined" || !window.gtag) return;
  
  // Track the call click event for GA4
  trackEvent('call_click', { location, phone: PHONE_NUMBER });
  
  // Fire Google Ads conversion for call clicks
  if (GOOGLE_ADS_CALL_CLICK_LABEL !== 'PASTE_CALL_CLICK_LABEL_HERE') {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CALL_CLICK_LABEL}`,
    });
    console.log('[CONVERSION] Google Ads call click conversion fired');
  } else {
    console.log('[CONVERSION] Call click label not configured - skipping Google Ads conversion');
  }
};


// Sticky Header Component
const StickyHeader = ({ onStartReview }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCallClick = () => {
    trackCallConversion('header');
  };

  return (
    <header
      data-testid="sticky-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-navy shadow-lg" : "bg-navy/95"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-8 w-8 text-gold" />
          <span className="font-serif text-white text-lg font-bold hidden sm:block">
            Auto Accident Claim Assist
          </span>
          <span className="font-serif text-white text-base font-bold sm:hidden">
            AACA
          </span>
        </div>
        
        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href={PHONE_LINK}
            onClick={handleCallClick}
            data-testid="header-call-btn"
            id="header-call-btn"
            className={`${CALL_CTA_CLASS} flex items-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-2.5 px-5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg`}
          >
            <Phone className="h-5 w-5" />
            Call Now: {PHONE_NUMBER}
          </a>
          <Button
            data-testid="header-review-btn"
            onClick={() => {
              onStartReview();
              trackEvent('survey_start', { location: 'header' });
            }}
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-navy font-bold py-2.5 px-5 rounded-full bg-transparent"
          >
            Get Free Review
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-navy border-t border-navy-50 px-4 py-4 animate-fade-in">
          <Button
            data-testid="mobile-review-btn"
            onClick={() => {
              onStartReview();
              setMobileMenuOpen(false);
              trackEvent('survey_start', { location: 'mobile_menu' });
            }}
            className="w-full bg-gold hover:bg-gold-600 text-white font-bold py-3 rounded-full"
          >
            Start Free Case Review
          </Button>
        </div>
      )}
    </header>
  );
};

// Sticky Mobile Bottom Bar
const StickyMobileBar = ({ onStartReview }) => {
  const handleCallClick = () => {
    trackCallConversion('mobile_bottom_bar');
  };

  return (
    <div 
      data-testid="sticky-mobile-bar"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] p-3"
    >
      <div className="flex gap-2">
        <a
          href={PHONE_LINK}
          onClick={handleCallClick}
          data-testid="mobile-bar-call-btn"
          id="mobile-bar-call-btn"
          className={`${CALL_CTA_CLASS} flex-1 flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-3.5 px-4 rounded-full transition-all shadow-md`}
        >
          <Phone className="h-5 w-5" />
          <span className="text-sm">Call Now</span>
        </a>
        <Button
          data-testid="mobile-bar-review-btn"
          onClick={() => {
            onStartReview();
            trackEvent('survey_start', { location: 'mobile_bottom_bar' });
          }}
          className="flex-1 bg-navy hover:bg-navy-50 text-white font-bold py-3.5 px-4 rounded-full"
        >
          <span className="text-sm">Free Claim Review</span>
        </Button>
      </div>
      <p className="text-center text-slate-500 text-xs mt-2">No obligation · No upfront cost</p>
    </div>
  );
};

// Trust Bar Component
const TrustBar = () => {
  const trustItems = [
    { icon: <MapPin className="h-4 w-4" />, text: "South Carolina Only" },
    { icon: <FileCheck className="h-4 w-4" />, text: "Free Case Review" },
    { icon: <Zap className="h-4 w-4" />, text: "Fast Response" },
    { icon: <Lock className="h-4 w-4" />, text: "100% Confidential" },
  ];

  return (
    <div data-testid="trust-bar" className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6">
      {trustItems.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5 text-slate-300 text-sm">
          <span className="text-gold">{item.icon}</span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  );
};

// Embedded Survey Component (for Hero)
const EmbeddedSurvey = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [surveyData, setSurveyData] = useState({
    hurt_in_accident: null,
    accident_date: null,
    at_fault: null,
    in_south_carolina: null,
    first_name: "",
    email: "",
    phone: "",
    consent: false,
    honeypot: "" // Spam protection
  });
  const [specialMessage, setSpecialMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const surveyStartedRef = useRef(false);

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  // Track survey start only once
  useEffect(() => {
    if (!surveyStartedRef.current) {
      surveyStartedRef.current = true;
      trackEvent('survey_start', { location: 'hero_embedded' });
    }
  }, []);

  const handleOptionSelect = (field, value) => {
    setSurveyData(prev => ({ ...prev, [field]: value }));
    setSpecialMessage(null);

    // Track step completion
    const stepMap = {
      hurt_in_accident: 1,
      accident_date: 2,
      at_fault: 3,
      in_south_carolina: 4
    };
    if (stepMap[field]) {
      trackEvent('survey_step_complete', { step: stepMap[field], field, value });
    }

    // Check for special messages
    if (field === "hurt_in_accident" && value === "No") {
      setSpecialMessage("We may still be able to help with property damage or next steps.");
    } else if (field === "at_fault" && value === "Yes") {
      setSpecialMessage("You may still have options.");
    } else if (field === "in_south_carolina" && value === "No") {
      setSpecialMessage("We currently focus on South Carolina claims.");
    }
  };

  const handleDateQuickOption = (option) => {
    let dateString = "";
    const today = new Date();
    
    switch(option) {
      case "today":
        dateString = format(today, "PPP");
        break;
      case "1-7":
        dateString = "Within the past week";
        break;
      case "8-30":
        dateString = "8-30 days ago";
        break;
      case "30+":
        dateString = "More than 30 days ago";
        break;
      default:
        dateString = option;
    }
    
    setSurveyData(prev => ({ ...prev, accident_date: dateString }));
    trackEvent('survey_step_complete', { step: 2, field: 'accident_date', value: option });
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      setSurveyData(prev => ({ ...prev, accident_date: format(date, "PPP") }));
      setDatePickerOpen(false);
      trackEvent('survey_step_complete', { step: 2, field: 'accident_date', value: 'calendar_pick' });
    }
  };

  const nextStep = () => {
    setSpecialMessage(null);
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setSpecialMessage(null);
    setStep(prev => prev - 1);
  };

  // Phone input masking
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, "");
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setSurveyData(prev => ({ ...prev, phone: formatted }));
    validateField('phone', formatted);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    return digits.length === 10;
  };

  const validateField = (field, value) => {
    let error = null;
    switch(field) {
      case 'first_name':
        if (!value || value.trim() === '') error = 'First name is required';
        break;
      case 'email':
        if (!value || value.trim() === '') error = 'Email is required';
        else if (!validateEmail(value)) error = 'Please enter a valid email';
        break;
      case 'phone':
        if (!value || value.trim() === '') error = 'Phone number is required';
        else if (!validatePhone(value)) error = 'Please enter a valid 10-digit phone';
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, surveyData[field]);
  };

  const canProceed = () => {
    switch(step) {
      case 1:
        return surveyData.hurt_in_accident !== null;
      case 2:
        return surveyData.accident_date !== null;
      case 3:
        return surveyData.at_fault !== null;
      case 4:
        return surveyData.in_south_carolina !== null;
      case 5:
        return (
          surveyData.first_name.trim() !== "" &&
          validateEmail(surveyData.email) &&
          validatePhone(surveyData.phone) &&
          surveyData.consent &&
          surveyData.honeypot === "" // Spam check
        );
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    // Validate all fields
    const isValidName = validateField('first_name', surveyData.first_name);
    const isValidEmail = validateField('email', surveyData.email);
    const isValidPhone = validateField('phone', surveyData.phone);
    
    if (!isValidName || !isValidEmail || !isValidPhone || !surveyData.consent) {
      setTouched({ first_name: true, email: true, phone: true });
      return;
    }

    // Honeypot spam check
    if (surveyData.honeypot !== "") {
      console.log("Spam detected");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare payload with ALL form fields (flat structure for webhook)
      const payload = {
      first_name: surveyData.first_name.trim(),
      email: surveyData.email.trim(),
      phone: surveyData.phone,
      consent: surveyData.consent,
      survey_answers: {
        hurt_in_accident: surveyData.hurt_in_accident,
        accident_date: surveyData.accident_date,
        at_fault: surveyData.at_fault,
        in_south_carolina: surveyData.in_south_carolina,
      },
    };

      console.log('[FORM] Submitting to API:', payload);

      // Submit to Vercel serverless function
      const response = await fetch(API_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log('[FORM] API response:', result);

      if (!response.ok) {
        throw new Error(result.detail || result.error || 'Submission failed');
      }

      setIsSubmitted(true);
      
      // Track successful submission for GA4
      trackEvent('lead_submit', {
        hurt_in_accident: surveyData.hurt_in_accident,
        in_south_carolina: surveyData.in_south_carolina,
        at_fault: surveyData.at_fault
      });
      
      // Fire Google Ads conversion for successful form submission
      trackFormConversion();
      
      toast.success("Your information has been submitted successfully!");
      if (onComplete) onComplete();
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("There was an error submitting your information. Please try calling us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const OptionButton = ({ value, selected, onClick, children, stepId }) => (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`option-${stepId}-${value.toLowerCase().replace(/\s+/g, '-')}`}
      className={`w-full p-3 sm:p-4 rounded-xl border-2 text-left font-semibold transition-all text-sm sm:text-base ${
        selected
          ? "border-gold bg-gold/10 text-navy"
          : "border-slate-200 bg-white text-slate-700 hover:border-gold/50 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <span>{children}</span>
        {selected && <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0" />}
      </div>
    </button>
  );

  // Confirmation / What Happens Next screen
  if (isSubmitted) {
    return (
      <div data-testid="survey-confirmation" className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
        <div className="text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="font-serif text-xl font-bold text-navy mb-2">Thank You!</h3>
          <p className="text-slate-600 text-sm mb-4">Your information has been submitted.</p>
        </div>
        
        {/* What Happens Next */}
        <div data-testid="what-happens-next" className="bg-slate-50 rounded-xl p-4 mb-4">
          <h4 className="font-semibold text-navy text-sm mb-3 text-center">What Happens Next</h4>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
              <p className="text-slate-700 text-sm">We review your case details</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
              <p className="text-slate-700 text-sm">We call you to discuss</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
              <p className="text-slate-700 text-sm">We connect you if eligible</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3 mb-4 text-center">
          <p className="text-slate-700 text-sm font-medium">
            <Clock className="h-4 w-4 inline mr-1" />
            Response within 15 min (business hours)
          </p>
          <p className="text-slate-500 text-xs">After hours: next business day</p>
        </div>
        
        <a
          href={PHONE_LINK}
          onClick={() => trackCallConversion('confirmation')}
          data-testid="confirmation-call-btn"
          id="confirmation-call-btn"
          className={`${CALL_CTA_CLASS} flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-3.5 px-6 rounded-full transition-all shadow-lg w-full animate-pulse-gold`}
        >
          <Phone className="h-5 w-5" />
          Call Now: {PHONE_NUMBER}
        </a>
        <p className="text-center text-slate-400 text-xs mt-2">Or wait for our call</p>
      </div>
    );
  }

  return (
    <div data-testid="embedded-survey" className="bg-white rounded-2xl shadow-xl p-5 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg font-bold text-navy">Free Case Review</h3>
        <span className="text-xs text-slate-500">Step {step}/5</span>
      </div>
      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
        <Clock className="h-3 w-3" /> Takes 60 seconds
      </p>
      
      {/* Progress Bar */}
      <Progress value={progress} className="h-1.5 mb-4 bg-slate-200" data-testid="survey-progress" />

      {/* Step Content */}
      <div className="min-h-[220px] animate-slide-in" key={step}>
        {/* Step 1: Were you hurt? */}
        {step === 1 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <AlertTriangle className="h-5 w-5 text-gold flex-shrink-0" />
              Were you hurt in a car accident?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="Yes"
                stepId="q1"
                selected={surveyData.hurt_in_accident === "Yes"}
                onClick={(v) => handleOptionSelect("hurt_in_accident", v)}
              >
                Yes, I was injured
              </OptionButton>
              <OptionButton
                value="No"
                stepId="q1"
                selected={surveyData.hurt_in_accident === "No"}
                onClick={(v) => handleOptionSelect("hurt_in_accident", v)}
              >
                No, but I have other damages
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 2: When was the accident? */}
        {step === 2 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="h-5 w-5 text-gold flex-shrink-0" />
              When was the accident?
            </h4>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <OptionButton
                value="today"
                stepId="q2"
                selected={surveyData.accident_date === format(new Date(), "PPP")}
                onClick={() => handleDateQuickOption("today")}
              >
                Today
              </OptionButton>
              <OptionButton
                value="1-7"
                stepId="q2"
                selected={surveyData.accident_date === "Within the past week"}
                onClick={() => handleDateQuickOption("1-7")}
              >
                1-7 days
              </OptionButton>
              <OptionButton
                value="8-30"
                stepId="q2"
                selected={surveyData.accident_date === "8-30 days ago"}
                onClick={() => handleDateQuickOption("8-30")}
              >
                8-30 days
              </OptionButton>
              <OptionButton
                value="30+"
                stepId="q2"
                selected={surveyData.accident_date === "More than 30 days ago"}
                onClick={() => handleDateQuickOption("30+")}
              >
                30+ days
              </OptionButton>
            </div>
            
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="date-picker-trigger"
                  className="w-full justify-start text-left font-normal text-sm h-10"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Or pick a specific date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={surveyData.accident_date ? new Date(surveyData.accident_date) : undefined}
                  onSelect={handleCalendarSelect}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Step 3: Were you at fault? */}
        {step === 3 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Car className="h-5 w-5 text-gold flex-shrink-0" />
              Were you at fault?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="No"
                stepId="q3"
                selected={surveyData.at_fault === "No"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                No, I was not at fault
              </OptionButton>
              <OptionButton
                value="Yes"
                stepId="q3"
                selected={surveyData.at_fault === "Yes"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                Yes, I was at fault
              </OptionButton>
              <OptionButton
                value="Not sure"
                stepId="q3"
                selected={surveyData.at_fault === "Not sure"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                I'm not sure
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 4: South Carolina? */}
        {step === 4 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <MapPin className="h-5 w-5 text-gold flex-shrink-0" />
              Was the accident in South Carolina?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="Yes"
                stepId="q4"
                selected={surveyData.in_south_carolina === "Yes"}
                onClick={(v) => handleOptionSelect("in_south_carolina", v)}
              >
                Yes, in South Carolina
              </OptionButton>
              <OptionButton
                value="No"
                stepId="q4"
                selected={surveyData.in_south_carolina === "No"}
                onClick={(v) => handleOptionSelect("in_south_carolina", v)}
              >
                No, in a different state
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 5: Contact Information */}
        {step === 5 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Phone className="h-5 w-5 text-gold flex-shrink-0" />
              Your Contact Information
            </h4>
            <div className="space-y-3">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={surveyData.honeypot}
                onChange={(e) => setSurveyData(prev => ({ ...prev, honeypot: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div>
                <Label htmlFor="first_name" className="text-slate-700 text-sm">First Name *</Label>
                <Input
                  id="first_name"
                  data-testid="input-first-name"
                  value={surveyData.first_name}
                  onChange={(e) => {
                    setSurveyData(prev => ({ ...prev, first_name: e.target.value }));
                    if (touched.first_name) validateField('first_name', e.target.value);
                  }}
                  onBlur={() => handleBlur('first_name')}
                  placeholder="John"
                  className={`mt-1 h-11 ${touched.first_name && errors.first_name ? 'border-red-500' : ''}`}
                />
                {touched.first_name && errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={surveyData.email}
                  onChange={(e) => {
                    setSurveyData(prev => ({ ...prev, email: e.target.value }));
                    if (touched.email) validateField('email', e.target.value);
                  }}
                  onBlur={() => handleBlur('email')}
                  placeholder="john@example.com"
                  className={`mt-1 h-11 ${touched.email && errors.email ? 'border-red-500' : ''}`}
                />
                {touched.email && errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-700 text-sm">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="input-phone"
                  value={surveyData.phone}
                  onChange={handlePhoneChange}
                  onBlur={() => handleBlur('phone')}
                  placeholder="(555) 123-4567"
                  className={`mt-1 h-11 ${touched.phone && errors.phone ? 'border-red-500' : ''}`}
                />
                {touched.phone && errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>
              <div className="flex items-start space-x-3 pt-1">
                <Checkbox
                  id="consent"
                  data-testid="checkbox-consent"
                  checked={surveyData.consent}
                  onCheckedChange={(checked) => setSurveyData(prev => ({ ...prev, consent: checked }))}
                  className="mt-0.5"
                />
                <Label htmlFor="consent" className="text-xs text-slate-600 leading-tight cursor-pointer">
                  I agree to be contacted by phone/text about my claim. Msg/data rates may apply. *
                </Label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-3 mt-4">
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={prevStep}
            data-testid="btn-back"
            className="flex-1 h-11"
          >
            Back
          </Button>
        ) : (
          <div className="flex-1" />
        )}
        
        {step < 5 ? (
          <Button
            onClick={nextStep}
            disabled={!canProceed()}
            data-testid="btn-next"
            className="flex-1 bg-gold hover:bg-gold-600 text-white h-11"
          >
            Continue
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canProceed() || isSubmitting}
            data-testid="btn-submit"
            className="flex-1 bg-gold hover:bg-gold-600 text-white h-11"
          >
            {isSubmitting ? "Submitting..." : "Get Free Review"}
          </Button>
        )}
      </div>
      
      {/* Friction reducers */}
      <div className="flex justify-center gap-4 mt-3 text-xs text-slate-500">
        <span>No obligation</span>
        <span>•</span>
        <span>No upfront cost</span>
      </div>
    </div>
  );
};

// Star Rating Component
const StarRating = ({ rating = 5, count = 127 }) => (
  <div className="flex items-center gap-1">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-gold text-gold' : 'text-slate-300'}`}
      />
    ))}
    <span className="text-sm text-slate-600 ml-1">({count} reviews)</span>
  </div>
);

// Hero Section Component with Embedded Survey
const HeroSection = ({ scrollToSurvey }) => {
  const handleCallClick = () => {
    trackCallConversion('hero');
  };

  return (
    <section data-testid="hero-section" className="bg-navy pt-20 pb-8 md:pt-24 md:pb-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Mobile: H1 Headline + Survey first */}
        <div className="md:hidden">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-4 text-white text-center">
            Get Help With Your South Carolina Car Accident Claim
          </h1>
          <p className="text-slate-300 text-center text-sm mb-6">
            Injured in an auto accident? See if you qualify for compensation.
          </p>
          <div className="mb-8">
            <EmbeddedSurvey />
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Left Content - Hidden H1 on mobile since we show it above */}
          <div className="text-white animate-fade-in order-2 md:order-1">
            <h1 className="hidden md:block font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight leading-tight mb-4">
              Get Help With Your South Carolina Car Accident Claim
            </h1>
            
            {/* Trust Bar */}
            <TrustBar />
            
            <p className="text-base sm:text-lg text-slate-300 mt-6 mb-6">
              Injured in an auto accident? You may be entitled to compensation for medical bills, lost wages, property damage, and more.
            </p>
            
            {/* Social Proof */}
            <div className="bg-navy-50/50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <StarRating rating={5} count={127} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-slate-200 text-sm italic">"They handled everything while I focused on recovery."</p>
                  <p className="text-gold text-xs mt-1 font-medium">— Sarah M., Charleston, SC</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-slate-200 text-sm italic">"Fast response and kept me updated every step."</p>
                  <p className="text-gold text-xs mt-1 font-medium">— James T., Columbia, SC</p>
                </div>
              </div>
            </div>
            
            {/* Benefits */}
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm sm:text-base">We fight for maximum compensation</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm sm:text-base">Fast response and clear guidance every step</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm sm:text-base">Local experts who know SC laws</span>
              </li>
            </ul>

            {/* Desktop CTA with friction reducers */}
            <div className="hidden md:block">
              <a
                href={PHONE_LINK}
                onClick={handleCallClick}
                data-testid="hero-call-btn"
                id="hero-call-btn"
                className={`${CALL_CTA_CLASS} inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-4 px-8 rounded-full transition-all hover:-translate-y-1 shadow-lg text-lg`}
              >
                <Phone className="h-6 w-6" />
                Call Now: {PHONE_NUMBER}
              </a>
              <p className="text-slate-400 text-sm mt-3">
                <span className="inline-flex items-center gap-4">
                  <span>No obligation</span>
                  <span>•</span>
                  <span>No upfront cost</span>
                  <span>•</span>
                  <span>You can call instead</span>
                </span>
              </p>
            </div>
          </div>

          {/* Right: Embedded Survey (Desktop) */}
          <div className="hidden md:block order-1 md:order-2">
            <EmbeddedSurvey />
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      icon: <Car className="h-8 w-8" />,
      title: "Share a few details",
      description: "Answer simple questions about your accident, injuries, and insurance situation."
    },
    {
      icon: <ShieldCheck className="h-8 w-8" />,
      title: "We review your case",
      description: "Your information is securely reviewed to understand your circumstances."
    },
    {
      icon: <Phone className="h-8 w-8" />,
      title: "We call to discuss",
      description: "You'll receive a call to discuss your case and explain your options."
    },
    {
      icon: <CheckCircle2 className="h-8 w-8" />,
      title: "Get connected if eligible",
      description: "If you choose to continue, we guide you through the next steps."
    }
  ];

  return (
    <section data-testid="how-it-works-section" className="py-12 md:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy text-center mb-3">
          How it Works
        </h2>
        <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
          Get the help you need in four simple steps. No upfront cost, no obligation.
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              data-testid={`step-card-${index + 1}`}
              className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow group"
            >
              <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center text-gold mb-4 group-hover:bg-gold group-hover:text-white transition-colors">
                {step.icon}
              </div>
              <div className="text-gold font-bold text-sm mb-2">Step {index + 1}</div>
              <h3 className="font-serif text-lg font-bold text-navy mb-2">{step.title}</h3>
              <p className="text-slate-600 text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section with stronger social proof
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "They stepped in right away, handled the insurance company for me, and kept me updated the whole time. I felt supported from the first call.",
      name: "Alex R.",
      location: "Charleston, SC",
      rating: 5
    },
    {
      quote: "They broke everything down in simple terms, handled the paperwork I was stressing over, and made sure I didn't miss any important deadlines.",
      name: "Helen P.",
      location: "Florence, SC",
      rating: 5
    },
    {
      quote: "They took over the entire claim and made sure nothing slipped through the cracks. Highly recommend to anyone in SC.",
      name: "Kevin S.",
      location: "Columbia, SC",
      rating: 5
    }
  ];

  return (
    <section data-testid="testimonials-section" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-3">
            What Our Clients Say
          </h2>
          <div className="flex justify-center mb-2">
            <StarRating rating={5} count={127} />
          </div>
          <p className="text-slate-600">
            Real stories from South Carolina accident victims we've helped.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              data-testid={`testimonial-card-${index + 1}`}
              className="bg-slate-50 rounded-xl p-6 border border-slate-100"
            >
              <div className="flex gap-0.5 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-slate-700 mb-4 text-sm leading-relaxed">"{testimonial.quote}"</p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-navy text-sm">{testimonial.name}</p>
                  <p className="text-slate-500 text-xs flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {testimonial.location}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "Do I need insurance to get help?",
      answer: "No, you don't necessarily need insurance to seek help after an accident. We can review your situation regardless of your insurance status and help determine your options, including UM/UIM coverage you might not be aware of."
    },
    {
      question: "What if I'm not sure who was at fault?",
      answer: "Fault can be complicated in auto accidents. A professional can review the circumstances of your accident and help explain possible paths forward. Many cases involve shared fault, and you may still be entitled to compensation."
    },
    {
      question: "What if injuries appeared later?",
      answer: "Many auto-related injuries show up days or weeks after an accident. This is very common, especially with soft-tissue injuries like whiplash. A review can still be helpful, and documenting these delayed injuries is important for your claim."
    },
    {
      question: "What if I already talked to an insurance adjuster?",
      answer: "You can still speak with someone to understand your options moving forward. It's important to know that insurance adjusters work for the insurance company, not for you. Getting independent advice can help protect your interests."
    },
    {
      question: "Is there any cost to start?",
      answer: "There's no upfront cost to speak with a professional you're matched with. Most auto accident attorneys work on a contingency basis, meaning they only get paid if you win your case."
    }
  ];

  return (
    <section data-testid="faq-section" className="py-12 md:py-20 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy text-center mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-slate-600 text-center mb-10">
          Get answers to common questions about auto accident claims.
        </p>
        
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              data-testid={`faq-item-${index + 1}`}
              className="bg-white rounded-xl border border-slate-200 px-6"
            >
              <AccordionTrigger className="text-left font-semibold text-navy hover:no-underline py-4 text-sm sm:text-base">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-4 text-sm">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onStartReview }) => {
  const handleCallClick = () => {
    trackCallConversion('cta_section');
  };

  return (
    <section data-testid="cta-section" className="py-12 md:py-16 bg-navy">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <ShieldCheck className="h-12 w-12 text-gold mx-auto mb-4" />
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-3">
          Ready to Check Your Case?
        </h2>
        <p className="text-slate-300 mb-6">
          See if you may qualify to speak with a South Carolina professional today.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
          <a
            href={PHONE_LINK}
            onClick={handleCallClick}
            data-testid="cta-call-btn"
            id="cta-call-btn"
            className={`${CALL_CTA_CLASS} flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-4 px-8 rounded-full transition-all hover:-translate-y-1 shadow-lg`}
          >
            <Phone className="h-5 w-5" />
            Call Now: {PHONE_NUMBER}
          </a>
          <Button
            data-testid="cta-review-btn"
            onClick={() => {
              onStartReview();
              trackEvent('survey_start', { location: 'cta_section' });
            }}
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-navy font-bold py-4 px-8 rounded-full bg-transparent h-auto"
          >
            Start Free Claim Check
          </Button>
        </div>
        
        <p className="text-slate-400 text-sm">
          No obligation · No upfront cost · You can call instead
        </p>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-navy-400 py-10 pb-24 md:pb-10">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-gold" />
            <span className="font-serif text-white text-base font-bold">
              Auto Accident Claim Assist
            </span>
          </div>
          <a
            href={PHONE_LINK}
            onClick={() => trackCallConversion('footer')}
            id="footer-call-btn"
            className={`${CALL_CTA_CLASS} flex items-center gap-2 text-white hover:text-gold transition-colors`}
          >
            <Phone className="h-5 w-5" />
            {PHONE_NUMBER}
          </a>
        </div>
        
        {/* Disclaimer */}
        <div className="border-t border-slate-700 pt-6">
          <p className="text-slate-400 text-xs leading-relaxed mb-3">
            <strong>Disclaimer:</strong> This website provides general information about auto accident claims in South Carolina and is not legal advice. Every case is unique, and past results do not guarantee future outcomes. Results depend on the specific facts of each case. By submitting your information, you consent to be contacted by phone, text, or email regarding your inquiry. Msg/data rates may apply. This is an attorney advertisement.
          </p>
        </div>
        
        <div className="border-t border-slate-700 mt-6 pt-4 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Auto Accident Claim Assist. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [showMobileBar, setShowMobileBar] = useState(true);
  const heroRef = useRef(null);

  const scrollToSurvey = useCallback(() => {
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    // On mobile, scroll to top since survey is first
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="App min-h-screen">
      <Toaster position="top-center" richColors />
      <StickyHeader onStartReview={scrollToSurvey} />
      <main ref={heroRef}>
        <HeroSection scrollToSurvey={scrollToSurvey} />
        <HowItWorksSection />
        <TestimonialsSection />
        <FAQSection />
        <CTASection onStartReview={scrollToSurvey} />
      </main>
      <Footer />
      {showMobileBar && <StickyMobileBar onStartReview={scrollToSurvey} />}
    </div>
  );
}

export default App;
